/**
 * Onglet "Plans d'usage" du Super Admin Crédits.
 * Permet de définir des plans regroupant des actions site avec un coût en crédits.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, Edit3, Archive, Coins, Save, X, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'general', label: 'Général' },
  { value: 'ftpe', label: 'FTPE' },
  { value: 'domium', label: 'DOMIUM' },
  { value: 'rcds', label: 'RCDS BATICO' },
  { value: 'ged', label: 'GED & Documents' },
  { value: 'divers', label: 'Divers' },
];

const COULEURS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#64748B'];

const emptyForm = () => ({
  nom: '',
  description: '',
  categorie: 'general',
  couleur: '#6366F1',
  actif: true,
  public: false,
  ordre: 0,
  actions: [],
});

const CreditUsagePlansTab = ({ getAuthHeader }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/admin/credit-usage-plans`, { headers: getAuthHeader() });
      setPlans(res.data?.plans || []);
    } catch {
      toast.error('Erreur de chargement des plans');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingPlan(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({ ...emptyForm(), ...plan, actions: plan.actions || [] });
    setModalOpen(true);
  };

  const addAction = () => {
    setForm((f) => ({
      ...f,
      actions: [...(f.actions || []), { code: '', libelle: '', cout_credits: 1, categorie: f.categorie, description: '', bloquant: false }],
    }));
  };

  const updateAction = (idx, field, value) => {
    setForm((f) => {
      const next = [...f.actions];
      next[idx] = { ...next[idx], [field]: field === 'cout_credits' ? parseInt(value || 0, 10) : value };
      return { ...f, actions: next };
    });
  };

  const removeAction = (idx) => {
    setForm((f) => ({ ...f, actions: f.actions.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Le nom du plan est requis'); return; }
    if (!form.actions || form.actions.some((a) => !a.code || !a.libelle)) {
      toast.error('Chaque action doit avoir un code et un libellé'); return;
    }
    try {
      setSavingId(editingPlan?.id || 'new');
      const payload = {
        nom: form.nom.trim(),
        description: form.description,
        categorie: form.categorie,
        couleur: form.couleur,
        actif: form.actif === true,
        public: form.public === true,
        ordre: parseInt(form.ordre || 0, 10),
        actions: form.actions.map((a) => ({
          code: a.code.trim(),
          libelle: a.libelle.trim(),
          cout_credits: parseInt(a.cout_credits || 0, 10),
          categorie: a.categorie || form.categorie,
          description: a.description || '',
          bloquant: a.bloquant === true,
        })),
      };
      if (editingPlan?.id) {
        await axios.put(`${API}/api/admin/credit-usage-plans/${editingPlan.id}`, payload, { headers: getAuthHeader() });
        toast.success('Plan mis à jour');
      } else {
        await axios.post(`${API}/api/admin/credit-usage-plans`, payload, { headers: getAuthHeader() });
        toast.success('Plan créé');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSavingId(null);
    }
  };

  const handleArchive = async (plan) => {
    if (!window.confirm(`Archiver le plan "${plan.nom}" ? Il ne sera plus visible mais reste consultable dans les archives.`)) return;
    try {
      await axios.delete(`${API}/api/admin/credit-usage-plans/${plan.id}`, { headers: getAuthHeader() });
      toast.success('Plan archivé');
      load();
    } catch {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  return (
    <div className="space-y-4" data-testid="credit-usage-plans-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Plans d'usage de crédits</h3>
          <p className="text-sm text-slate-500">
            Définissez les actions du site facturées en crédits et regroupez-les en plans réutilisables.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="usage-plan-create-btn">
          <Plus className="h-4 w-4 mr-1" /> Nouveau plan
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Chargement…</div>
      ) : plans.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-slate-500">
            <Coins className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            Aucun plan d'usage défini. Cliquez sur « Nouveau plan » pour commencer.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const total = (plan.actions || []).reduce((s, a) => s + (a.cout_credits || 0), 0);
            return (
              <Card key={plan.id} className="border-l-4" style={{ borderLeftColor: plan.couleur || '#6366F1' }} data-testid={`usage-plan-card-${plan.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <CardTitle className="text-base">{plan.nom}</CardTitle>
                      <CardDescription className="text-xs">
                        <Badge variant="secondary" className="mr-1">{(CATEGORIES.find((c) => c.value === plan.categorie) || {}).label || plan.categorie}</Badge>
                        {plan.public ? (
                          <span className="text-emerald-600 inline-flex items-center gap-1"><Eye className="h-3 w-3" />Public</span>
                        ) : (
                          <span className="text-slate-400 inline-flex items-center gap-1"><EyeOff className="h-3 w-3" />Interne</span>
                        )}
                      </CardDescription>
                    </div>
                    <Badge variant={plan.actif ? 'default' : 'secondary'}>{plan.actif ? 'Actif' : 'Inactif'}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {plan.description && (
                    <p className="text-xs text-slate-500 line-clamp-2">{plan.description}</p>
                  )}
                  <div className="border rounded-md divide-y">
                    {(plan.actions || []).slice(0, 4).map((a, idx) => (
                      <div key={a.code + idx} className="flex items-center justify-between px-2 py-1.5 text-xs">
                        <span className="truncate"><span className="font-medium">{a.libelle}</span> <span className="text-slate-400">({a.code})</span></span>
                        <Badge variant="outline">{a.cout_credits} cr.</Badge>
                      </div>
                    ))}
                    {(plan.actions || []).length === 0 && (
                      <div className="px-2 py-2 text-xs text-slate-400 italic">Aucune action paramétrée.</div>
                    )}
                    {(plan.actions || []).length > 4 && (
                      <div className="px-2 py-1 text-[11px] text-slate-400">+ {plan.actions.length - 4} autre(s) action(s)…</div>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Total pack : <strong className="text-slate-700">{total} crédits</strong></span>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(plan)} data-testid={`usage-plan-edit-${plan.id}`}>
                        <Edit3 className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleArchive(plan)} className="text-red-500 hover:text-red-700" data-testid={`usage-plan-archive-${plan.id}`}>
                        <Archive className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal créer/éditer */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Modifier le plan' : 'Nouveau plan d\'usage'}</DialogTitle>
            <DialogDescription>
              Un plan regroupe une liste d'actions consommatrices et leur coût en crédits.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="md:col-span-2">
              <Label>Nom du plan *</Label>
              <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Ex: Plan FTPE Premium" data-testid="usage-plan-form-nom" />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Couleur</Label>
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {COULEURS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-label={`Couleur ${c}`}
                    onClick={() => setForm({ ...form, couleur: c })}
                    className={`h-7 w-7 rounded-full border-2 ${form.couleur === c ? 'border-slate-700 ring-2 ring-slate-200' : 'border-transparent'}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.actif === true} onCheckedChange={(v) => setForm({ ...form, actif: v })} />
              <span className="text-sm">Plan actif</span>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.public === true} onCheckedChange={(v) => setForm({ ...form, public: v })} />
              <span className="text-sm">Visible Cooper/Membre</span>
            </div>
          </div>

          <div className="border rounded-lg p-3 bg-slate-50/60">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-sm text-slate-700">Actions du plan</h4>
              <Button size="sm" variant="outline" onClick={addAction} data-testid="usage-plan-add-action">
                <Plus className="h-3.5 w-3.5 mr-1" />Ajouter une action
              </Button>
            </div>
            <div className="space-y-2">
              {form.actions.map((a, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`usage-plan-action-row-${idx}`}>
                  <div className="col-span-3">
                    <Label className="text-[11px] text-slate-500">Code</Label>
                    <Input value={a.code} onChange={(e) => updateAction(idx, 'code', e.target.value)} placeholder="ftpe_attestation" className="h-9" data-testid={`usage-plan-action-code-${idx}`} />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-[11px] text-slate-500">Libellé</Label>
                    <Input value={a.libelle} onChange={(e) => updateAction(idx, 'libelle', e.target.value)} placeholder="Émettre attestation FTPE" className="h-9" data-testid={`usage-plan-action-libelle-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[11px] text-slate-500">Coût (cr.)</Label>
                    <Input type="number" min={0} value={a.cout_credits} onChange={(e) => updateAction(idx, 'cout_credits', e.target.value)} className="h-9 text-center" data-testid={`usage-plan-action-cost-${idx}`} />
                  </div>
                  <div className="col-span-2 flex flex-col items-center">
                    <Label className="text-[11px] text-slate-500">Bloquant</Label>
                    <Switch
                      checked={a.bloquant === true}
                      onCheckedChange={(v) => updateAction(idx, 'bloquant', v)}
                      data-testid={`usage-plan-action-bloquant-${idx}`}
                      title="Si activé, l'action est refusée (HTTP 402) en cas de solde insuffisant"
                    />
                  </div>
                  <div className="col-span-1 text-center">
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeAction(idx)} data-testid={`usage-plan-action-remove-${idx}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {form.actions.length === 0 && (
                <p className="text-xs text-slate-400 italic text-center py-3">Aucune action. Cliquez sur « Ajouter une action ».</p>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={savingId !== null} data-testid="usage-plan-save-btn">
              <Save className="h-4 w-4 mr-1" />Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditUsagePlansTab;
